import { useCallback, useEffect, useState } from 'react'
import { Card, Table, Button, Space, Input, Select, DatePicker, Tag, App } from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { exportLogsApi } from '../../services/api'

const { RangePicker } = DatePicker

type LogRow = {
  key: string
  exporterName: string
  account: string
  exportTime: string
  module: string
  exportFields: string
  rowCount: number
  ipAddress: string
  status: string
}

function triggerDownloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const ExportLogManagement = () => {
  const { message } = App.useApp()
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [data, setData] = useState<LogRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [keyword, setKeyword] = useState('')
  const [module, setModule] = useState<string | undefined>()
  const [status, setStatus] = useState<string | undefined>()
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)

  const buildFilters = useCallback(() => {
    const filters: Record<string, string> = {}
    if (keyword.trim()) filters.keyword = keyword.trim()
    if (module) filters.module = module
    if (status) filters.status = status
    if (dateRange?.[0] && dateRange?.[1]) {
      filters.dateFrom = dateRange[0].format('YYYY-MM-DD')
      filters.dateTo = dateRange[1].format('YYYY-MM-DD')
    }
    return Object.keys(filters).length ? filters : undefined
  }, [keyword, module, status, dateRange])

  const fetchList = useCallback(() => {
    setLoading(true)
    const filters = buildFilters()
    exportLogsApi
      .list({ page, pageSize, filters })
      .then((res: unknown) => {
        const raw = res as { data?: { items?: LogRow[]; total?: number } }
        const items = raw?.data?.items ?? []
        setData(
          items.map((it, i) => ({
            ...it,
            key: String((it as LogRow).exportTime ?? i) + i,
            rowCount: Number((it as LogRow).rowCount ?? 0),
          })),
        )
        setTotal(Number(raw?.data?.total ?? items.length))
      })
      .catch(() => {
        setData([])
        setTotal(0)
        message.error('加载导出日志失败')
      })
      .finally(() => setLoading(false))
  }, [page, pageSize, buildFilters, message])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await exportLogsApi.export({ filters: buildFilters() })
      if (res instanceof Blob) {
        triggerDownloadBlob(res, `导出日志_${dayjs().format('YYYYMMDD_HHmmss')}.csv`)
        message.success('日志导出成功')
      }
    } catch {
      message.error('日志导出失败')
    } finally {
      setExporting(false)
    }
  }

  const columns = [
    { title: '导出人', dataIndex: 'exporterName', key: 'exporterName', width: 160 },
    { title: '账号', dataIndex: 'account', key: 'account', width: 120 },
    { title: '导出时间', dataIndex: 'exportTime', key: 'exportTime', width: 170 },
    { title: '导出模块', dataIndex: 'module', key: 'module', width: 100 },
    { title: '导出字段', dataIndex: 'exportFields', key: 'exportFields', ellipsis: true },
    { title: '导出条数', dataIndex: 'rowCount', key: 'rowCount', width: 90 },
    { title: 'IP地址', dataIndex: 'ipAddress', key: 'ipAddress', width: 170 },
    {
      title: '操作状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s: string) => (
        <Tag color={s === 'success' ? 'green' : 'red'}>{s === 'success' ? '成功' : '失败'}</Tag>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">导出日志</h1>
        <p className="text-gray-600">查看与导出资产数据导出操作记录</p>
      </div>

      <Card title="筛选条件">
        <Space wrap className="w-full">
          <Input
            placeholder="导出人 / 账号 / 模块"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Select
            placeholder="导出模块"
            allowClear
            style={{ width: 140 }}
            value={module}
            onChange={setModule}
            options={[{ value: '资产数据', label: '资产数据' }]}
          />
          <Select
            placeholder="操作状态"
            allowClear
            style={{ width: 120 }}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'success', label: '成功' },
              { value: 'failure', label: '失败' },
            ]}
          />
          <RangePicker value={dateRange} onChange={(d) => setDateRange(d as [Dayjs | null, Dayjs | null] | null)} />
          <Button type="primary" onClick={() => { setPage(1); fetchList() }}>
            查询
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => { setKeyword(''); setModule(undefined); setStatus(undefined); setDateRange(null); setPage(1); fetchList() }}>
            重置
          </Button>
        </Space>
      </Card>

      <Card
        title="日志列表"
        extra={
          <Button type="primary" icon={<DownloadOutlined />} loading={exporting} onClick={() => void handleExport()}>
            导出日志
          </Button>
        }
      >
        <Table
          rowKey="key"
          loading={loading}
          columns={columns}
          dataSource={data}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
          scroll={{ x: 1100 }}
        />
      </Card>
    </div>
  )
}

export default ExportLogManagement
